import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Input } from '@/components/ui/Input';
import { cn } from '@/lib/utils';

/** Способы тренировки словаря — в том порядке, что в макете. */
/**
 * Способы тренировки.
 *
 * ⚠️ ВЕДУТ В ТРЕНИРОВКУ СЛОВ, И ТОЛЬКО. До 06.09.2026 два пункта вели на общие
 * тренажёры IELTS: со словарём они не работают вовсе, а закрыты ДРУГИМИ ключами
 * тарифа — ученик со словарём упирался в замок внутри собственного словаря.
 *
 * ⚠️ У КАЖДОГО СПОСОБА СВОЙ СЧЁТ. Слово становится «Выучено», только когда
 * закрыты все пять. Поэтому способ едет в адресе: тренировка должна знать, что
 * именно засчитывать, иначе ответ уйдёт не в тот счёт.
 */
type Mode = {
  to: string;
  icon: string;
  tone: 'primary' | 'info';
  title: string;
  hint: string;
  /** Ключ способа — по нему берём «освоено». Пусто у строки «Всё подряд». */
  key?: string;
};

const MODES: Mode[] = [
  { to: '/app/cue-cards/my', icon: 'done_all', tone: 'primary', title: 'vocab.modeAll', hint: 'vocab.modeAllHint' },
  { to: '/app/cue-cards/my?mode=card', icon: 'style', tone: 'primary', title: 'vocab.modeCards', hint: 'vocab.modeCardsHint', key: 'card' },
  { to: '/app/cue-cards/my?mode=choose', icon: 'fact_check', tone: 'info', title: 'vocab.modeChoose', hint: 'vocab.modeChooseHint', key: 'choose' },
  { to: '/app/cue-cards/my?mode=sentence', icon: 'edit_note', tone: 'primary', title: 'vocab.modeSentence', hint: 'vocab.modeSentenceHint', key: 'sentence' },
  { to: '/app/cue-cards/my?mode=audio', icon: 'headphones', tone: 'info', title: 'vocab.modeAudio', hint: 'vocab.modeAudioHint', key: 'audio' },
  { to: '/app/cue-cards/my?mode=type', icon: 'spellcheck', tone: 'primary', title: 'vocab.modeType', hint: 'vocab.modeTypeHint', key: 'type' },
];

/**
 * Правая колонка словаря (макет «Desktop Web v2»): счёт по словам, способы
 * тренировки и добавление своего слова.
 *
 * ⚠️ Столбиков «по дням недели» из макета здесь НЕТ. Мы нигде не храним, сколько
 * слов добавлено в каждый день, — нарисовать их можно было бы только выдумав
 * числа. Вместо них — счёт, который у нас настоящий.
 */
export function VocabSidebar({
  total,
  learned,
  modes,
  learning,
  fresh,
  newWord,
  onNewWord,
  onAdd,
  adding,
  justAdded,
  addError,
}: {
  total: number;
  learned: number;
  /** Освоено по каждому способу: способ → сколько слов закрыто. */
  modes: Record<string, number>;
  /** Начатые, но ещё не осевшие. */
  learning: number;
  /** Ни разу не тренировались. */
  fresh: number;
  newWord: string;
  onNewWord: (v: string) => void;
  onAdd: () => void;
  adding: boolean;
  /** Слово только что добавлено — показываем галочку вместо подписи. */
  justAdded?: boolean;
  addError?: string;
}) {
  const { t } = useTranslation();

  return (
    <div>
      <div className="rounded-[22px] bg-surface-container px-5 py-[18px]">
        <h3 className="mb-3.5 text-[13.5px] font-bold text-on-surface">{t('vocab.myCount')}</h3>
        <div className="flex justify-between">
          {[
            /* ⚠️ ЭТИ ЧЕТЫРЕ ЧИСЛА ОБЯЗАНЫ СХОДИТЬСЯ: всего = новые + учу + в
               памяти. Раньше на месте «новых» и «учу» стояли «повтор» и
               «избранное»: первое — сколько пора СЕЙЧАС (это уже написано
               крупно в плашке сверху), второе всегда показывало ноль, потому
               что счёт избранного никогда не приходил с сервера. Ученик видел
               «всего 7, выучено 0, повтор 5» и не понимал, где ещё два. */
            { v: total, l: t('learn.wordsTotal'), c: 'text-on-surface' },
            { v: fresh, l: t('vocab.statFresh'), c: 'text-on-surface' },
            { v: learning, l: t('vocab.statLearning'), c: 'text-on-primary-container' },
            { v: learned, l: t('learn.wordsMastered'), c: 'text-info' },
          ].map((x) => (
            <div key={x.l}>
              <div className={cn('font-display text-[18px] font-bold tabular-nums', x.c)}>{x.v}</div>
              <div className="text-[11px] text-on-surface-variant">{x.l}</div>
            </div>
          ))}
        </div>
      </div>

      <h3 className="mx-0.5 mb-2.5 mt-[18px] font-display text-[15.5px] font-bold text-on-surface">
        {t('vocab.practiceModes')}
      </h3>
      <div className="rounded-[20px] bg-surface-container px-[18px] py-0.5">
        {MODES.map((m) => (
          <Link key={m.to} to={m.to} className="flex items-center gap-3 border-b border-outline py-3 last:border-b-0">
            <span
              className={cn(
                'grid h-[34px] w-[34px] flex-none place-items-center rounded-xl',
                m.tone === 'info' ? 'bg-info-container' : 'bg-primary-container',
              )}
            >
              <span className={cn('material-symbols-outlined text-[18px]', m.tone === 'info' ? 'text-info' : 'text-primary')}>
                {m.icon}
              </span>
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-[13px] font-semibold text-on-surface">{t(m.title)}</span>
              <span className="block text-[11.5px] text-on-surface-variant">{t(m.hint)}</span>
            </span>
            {/* ⚠️ БЕЙДЖ — ГЛАВНОЕ ЗДЕСЬ, А НЕ УКРАШЕНИЕ. Способы разделены, и без
                числа ученик жмёт «Составить предложение» вслепую: непонятно,
                каким словам это ещё нужно. Показываем «освоено из всех слов». */}
            <span className="flex flex-none items-center gap-1.5">
              <span className="rounded-pill bg-surface-container-high px-2 py-0.5 text-[11.5px] font-bold tabular-nums text-on-surface-variant">
                {m.key ? (modes[m.key] ?? 0) : learned}/{total}
              </span>
              <span className="material-symbols-outlined text-[16px] text-on-surface-variant">chevron_right</span>
            </span>
          </Link>
        ))}
      </div>
        {/* ⚠️ БЕЗ ЭТОГО ВЫУЧЕННОЕ СЛОВО ПРОПАДАЛО НАВСЕГДА. Обычная тренировка
            берёт только несданное, а другого способа освежить выученное не было
            вовсе. Пометка «Выучено» по решению заказчика сама не сбрасывается —
            значит повторение должно быть отдельной дверью, иначе слово уходит
            из тренировки навсегда. Ошибка здесь возвращает слово в «Учу». */}
        {learned > 0 && (
          <Link
            to="/app/cue-cards/my?scope=learned"
            className="mt-3.5 flex items-center gap-3 rounded-[20px] bg-surface-container px-[18px] py-3"
          >
            <span className="grid h-[34px] w-[34px] flex-none place-items-center rounded-xl bg-info-container">
              <span className="material-symbols-outlined text-[18px] text-info">history</span>
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-[13px] font-semibold text-on-surface">{t('vocab.reviewLearned')}</span>
              <span className="block text-[11.5px] text-on-surface-variant">{t('vocab.reviewLearnedHint')}</span>
            </span>
            <span className="text-[13px] font-bold tabular-nums text-info">{learned}</span>
          </Link>
        )}


      {/* Своё слово: поле сразу здесь, а не за отдельной кнопкой — так на
          один шаг меньше, а места оно занимает столько же. */}
      <div className="mt-3.5 rounded-[20px] bg-surface-container px-[18px] py-[15px]">
        <div className="mb-2.5 flex items-center gap-3">
          <span className="grid h-[38px] w-[38px] flex-none place-items-center rounded-xl bg-primary-container">
            <span className="material-symbols-outlined text-[18px] text-primary">add</span>
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-[13px] font-semibold text-on-surface">{t('vocab.addWord')}</span>
            <span className="block text-[11.5px] text-on-surface-variant">{t('vocab.addWordHint')}</span>
          </span>
        </div>
        <div className="flex gap-2">
          <Input
            value={newWord}
            onChange={(e) => onNewWord(e.target.value)}
            placeholder={t('vocab.addWordPlaceholder')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newWord.trim() && !adding) onAdd();
            }}
          />
          {/* 🔴 РАНЬШЕ КНОПКА ПРОСТО БЛОКИРОВАЛАСЬ И ВЫГЛЯДЕЛА ТАК ЖЕ. Человек не
              понимал, идёт работа или нет, жал второй раз — и в словаре появлялось
              два одинаковых слова, а школа дважды платила за разбор и озвучку.
              Теперь видно и работу, и её конец. */}
          <button
            type="button"
            onClick={onAdd}
            disabled={!newWord.trim() || adding || justAdded}
            className="flex flex-none items-center gap-1.5 rounded-full bg-primary px-4 text-[12px] font-bold text-on-primary transition hover:bg-primary-hover disabled:opacity-60"
          >
            {adding && (
              <span className="material-symbols-outlined animate-spin text-[15px]">progress_activity</span>
            )}
            {justAdded && <span className="material-symbols-outlined text-[15px]">check</span>}
            {adding ? t('vocab.adding') : justAdded ? t('vocab.added') : t('vocab.add')}
          </button>
        </div>
        {addError && <p className="mt-2 text-[11.5px] text-error">{addError}</p>}
      </div>
    </div>
  );
}
